<div class="my-8 mx-auto flex flex-col gap-4">
  <!-- Header -->
  <div class="flex justify-between items-center w-full">
    <div class="flex items-center">
      <app-btn-back routeTo=".." btnText="Back"></app-btn-back>
    </div>

    @if (group?.titheCollectionEnabled) {
      <app-btn-action (clicked)="openTitheModal()" label="Pay Tithe"
        bgColor="bg-primary-500 hover:bg-primary-700 disabled:bg-gray-200"
        icon="fa fa-hand-holding-dollar"></app-btn-action>
    }
  </div>

  <div class="flex items-center">
    <h1 class="text-2xl font-bold text-primary-500 tracking-tight">My Giving</h1>
  </div>

  <!-- Left Column: Summary -->
  <div class="flex flex-col gap-4">

    <!-- Fiscal Year Summary Card -->
    <div
      class="relative overflow-hidden rounded-2xl bg-gradient-to-tr from-primary-500 to-secondary-500 p-6 text-white shadow-lg">
      <span class="text-xs font-semibold uppercase tracking-wider text-gray-100 opacity-80">This Fiscal Year</span>
      <h2 class="mt-2 text-4xl font-black tracking-tight">
        {{ totalFiscalYearContribution | currency: (group?.currency || 'USD') }}
      </h2>
      <div class="mt-4 border-t border-teal-400 border-opacity-30 pt-3">
        @if (fiscalYearStart) {
        <p class="text-xs text-teal-50">
          Fiscal Year Range:<br />
          <span class="font-semibold">{{ fiscalYearStart | date: 'mediumDate' }}</span> to
          <span class="font-semibold">{{ fiscalYearEnd ? (fiscalYearEnd | date: 'mediumDate') : 'Present' }}</span>
        </p>
        } @else {
        <p class="text-xs text-teal-100 opacity-80">
          No active fiscal year range defined. Contributions sum up all historical payments.
        </p>
        }
      </div>
    </div>

  </div>

  <!-- Right Column: Funding Campaigns & Transactions list -->
  <div class="flex flex-col gap-4">

    <!-- Active Funding Requests Card -->
    @if (activeFundings.length > 0) {
    <div class="rounded-2xl bg-white p-6 shadow-md border border-blueGray-100">
      <div class="flex justify-between items-center mb-4">
        <h3 class="text-lg font-bold text-blueGray-800">Fundraisers</h3>
        <a [routerLink]="['../funding']"
          class="text-sm font-semibold text-emerald-600 hover:text-emerald-700 transition-colors">
          View All
        </a>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        @for (fund of activeFundings.slice(0, 2); track fund.id) {
        <div
          class="border border-blueGray-100 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow relative flex flex-col justify-between">
          <div>
            <h4 class="font-bold text-blueGray-800">{{ fund.title }}</h4>
            <p class="text-xs text-blueGray-500 mt-2 mb-4 leading-relaxed line-clamp-3">{{ fund.description }}</p>
          </div>
          <div class="border-t border-blueGray-50 pt-3">
            @let target = fund.targetAmount || 0;
            @let raised = fund.raisedAmount || 0;
            @let progressPercent = target > 0 ? (raised / target * 100) : 0;
            @let clampedProgress = progressPercent > 100 ? 100 : progressPercent;

            <div class="w-full mb-3">
              <div class="flex justify-between items-center text-[10px] mb-1 font-bold">
                <span class="text-emerald-600">Progress: {{ progressPercent | number: '1.0-0' }}%</span>
                <span class="text-blueGray-500">{{ raised | currency: (group?.currency || 'USD') }} / {{ target |
                  currency: (group?.currency || 'USD') }}</span>
              </div>
              <div class="w-full bg-blueGray-100 rounded-full h-1.5 overflow-hidden">
                <div class="bg-emerald-600 h-1.5 rounded-full transition-all duration-500"
                  [style.width.%]="clampedProgress"></div>
              </div>
            </div>

            <div class="flex justify-between items-center mt-3">
              <div>
                <span class="text-[10px] uppercase font-semibold text-blueGray-400 block">Goal Target</span>
                <span class="text-sm font-extrabold text-emerald-600">{{ target | currency: (group?.currency || 'USD')
                  }}</span>
              </div>
              <button (click)="selectFundingForDonation(fund)"
                class="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs py-2 px-3 rounded-lg shadow transition-all focus:outline-none">
                Support Fund
              </button>
            </div>
          </div>
        </div>
        }
      </div>
    </div>
    }
  </div>

  <!-- Giving History Card -->
  <div class="flex flex-col justify-between gap-4">
    <span class="flex w-full items-center text-start text-md font-semibold text-primary-400">Giving History</span>

    @if (displayedPayments.length === 0) {
    <div class="flex flex-col items-center justify-center py-12 text-blueGray-400">
      <i class="fa fa-receipt text-4xl mb-3"></i>
      <p class="text-sm">No transaction records found.</p>
    </div>
    } @else {
    <div class="flex flex-col gap-3">
      @for (payment of displayedPayments; track payment.id) {
      <div
        class="relative flex flex-col gap-2 w-full bg-white border border-gray-200 rounded-xl p-4 hover:bg-slate-50 transition-colors">

        <!-- First Row: Date & Status Chip -->
        <div class="flex flex-row justify-between items-center w-full">
          <div class="flex flex-row items-center gap-2">
            <span class="text-sm text-primary-300">
              {{ getPaymentDate(payment) | date: 'mediumDate' }}
            </span>
            <span class="text-xs font-light px-2 py-0.5 rounded bg-gray-200 text-primary-400">
              {{ payment.type === 'tithe' ? 'Tithe' : (payment.type === 'fund_donation' ? 'Fund' : 'Donation') }}
            </span>
          </div>
          <app-chip [label]="payment.status | titlecase"
            [color]="payment.status === 'succeeded' || payment.status === 'active' ? chipColor.SUCCESS : chipColor.WARNING">
          </app-chip>
        </div>

        <!-- Second Row: Type & Amount & Description -->
        <div class="flex flex-row flex-wrap justify-between items-center w-full gap-2">
          <span class="text-sm text-primary-500">
            {{ getPaymentDescription(payment) }}
          </span>
          <span class="text-base uppercase font-black text-primary-500">
            {{ payment.amount | currency: (payment.currency || 'USD') }}
          </span>
        </div>

      </div>
      }
    </div>
    }
  </div>

  @if (hasMore) {
  <div class="mt-6 flex justify-center">
    <button (click)="loadMore()"
      class="flex items-center gap-2 px-6 py-2 border border-emerald-500 text-emerald-600 hover:bg-emerald-50 font-semibold text-sm rounded-lg transition-colors">
      Load More <i class="fa fa-chevron-down"></i>
    </button>
  </div>
  }
</div>

<!-- Donation Details Picker Modal Overlay -->
@if (showPaymentModal) {
<div class="fixed inset-0 bg-blueGray-900 bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
  <div
    class="bg-white rounded-2xl max-w-md w-full shadow-2xl p-6 border border-blueGray-100 relative animate-in fade-in zoom-in duration-200">
    <button (click)="cancelDonation()"
      class="absolute right-4 top-4 text-blueGray-400 hover:text-blueGray-600 focus:outline-none">
      <i class="fa fa-times text-lg"></i>
    </button>

    <span class="text-xs font-bold uppercase tracking-wider text-secondary-500">
      {{ selectedFunding ? 'Support Fund' : 'Pay Tithe' }}
    </span>
    <h3 class="text-xl font-bold text-blueGray-800 mt-1 mb-2">
      {{ selectedFunding ? selectedFunding.title : ('Tithe to ' + groupName) }}
    </h3>
    <p class="text-xs text-blueGray-500 mb-6 leading-relaxed">
      {{ selectedFunding ? selectedFunding.description : 'Your contribution directly supports our community activities
      and operations.' }}
    </p>

    <form [formGroup]="pageForm" (ngSubmit)="paymentStep === 'amount' ? proceedToPayment() : donate()"
      class="flex flex-col gap-4">
      @if (paymentStep === 'amount') {
      <!-- Currency -->
      <div>
        <label class="block text-xs font-semibold uppercase text-blueGray-500 mb-1">Currency</label>
        <div
          class="w-full rounded-lg border border-blueGray-200 bg-blueGray-100 p-2.5 text-sm text-blueGray-600 font-semibold select-none">
          {{ (group?.currency || 'USD') | uppercase }} (Church Base Currency)
        </div>
      </div>

      <!-- Amount -->
      <div>
        <label class="block text-xs font-semibold uppercase text-blueGray-500 mb-1">Amount</label>
        <div class="relative">
          <input type="number" formControlName="amount" placeholder="0.00"
            class="w-full pl-3 pr-3 py-2.5 rounded-lg border border-blueGray-200 bg-blueGray-50 text-sm text-blueGray-700 focus:border-secondary-500 focus:outline-none" />
        </div>
        @if (pageForm.get('amount')?.invalid && pageForm.get('amount')?.touched) {
        <span class="text-xs text-red-500 mt-1">Please enter a valid amount.</span>
        }
      </div>

      <!-- Frequency -->
      <div>
        <label class="block text-xs font-semibold uppercase text-blueGray-500 mb-2">Frequency</label>
        <div class="grid grid-cols-2 gap-2">
          <label
            class="cursor-pointer flex items-center justify-center p-2.5 rounded-lg border text-sm font-semibold transition-all"
            [ngClass]="pageForm.value.mode === 'payment' ? 'border-secondary-500 bg-blue-50 text-secondary-700' : 'border-blueGray-200 text-blueGray-600 hover:bg-blueGray-50'">
            <input type="radio" formControlName="mode" value="payment" class="sr-only" />
            One-time
          </label>
          <label
            class="cursor-pointer flex items-center justify-center p-2.5 rounded-lg border text-sm font-semibold transition-all"
            [ngClass]="pageForm.value.mode === 'subscription' ? 'border-secondary-500 bg-blue-50 text-secondary-700' : 'border-blueGray-200 text-blueGray-600 hover:bg-blueGray-50'">
            <input type="radio" formControlName="mode" value="subscription" class="sr-only" />
            Monthly
          </label>
        </div>
      </div>

      <!-- Submit/Next -->
      <div class="flex gap-2 mt-2">
        <button type="button" (click)="cancelDonation()"
          class="w-1/2 border border-blueGray-200 text-blueGray-600 hover:bg-blueGray-50 font-bold py-3 px-4 rounded-xl transition-all focus:outline-none">
          Cancel
        </button>
        <button type="submit" [disabled]="pageForm.invalid || stripeLoading"
          class="w-1/2 flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white font-bold py-3 px-4 rounded-xl shadow-lg transition-all focus:outline-none">
          @if (stripeLoading) {
          <i class="fa fa-spinner fa-spin mr-2"></i> Loading...
          } @else {
          Next <i class="fa fa-arrow-right ml-2"></i>
          }
        </button>
      </div>
      } @else {
      <!-- Step 2: Stripe Elements Card Form -->
      <div class="border-b border-blueGray-100 pb-3 mb-2 flex justify-between items-center">
        <span class="text-sm font-semibold text-blueGray-600">Amount: <span class="font-bold text-primary-500">{{
            pageForm.value.amount | currency: (group?.currency || 'USD') }}</span></span>
        <span
          class="text-xs text-blueGray-400 capitalize font-semibold bg-blue-50 text-secondary-600 px-2.5 py-1 rounded-full">{{
          pageForm.value.mode === 'payment' ? 'One-time' : 'Monthly' }} donation</span>
      </div>

      @if (stripeLoading) {
      <div class="flex flex-col items-center justify-center py-8">
        <i class="fa fa-spinner fa-spin text-2xl text-secondary-500 mb-2"></i>
        <span class="text-xs text-blueGray-500">Initializing secure payment form...</span>
      </div>
      }

      <div [class.hidden]="stripeLoading" id="payment-element" class="min-h-[150px]"></div>

      <div class="flex gap-2 mt-4">
        <button type="button" (click)="resetPaymentStep()" [disabled]="isSubmitting"
          class="w-1/2 border border-blueGray-200 text-blueGray-600 hover:bg-blueGray-50 font-bold py-3 px-4 rounded-xl transition-all focus:outline-none">
          Back
        </button>
        <button type="submit" [disabled]="pageForm.invalid || isSubmitting || stripeLoading"
          class="w-1/2 flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white font-bold py-3 px-4 rounded-xl shadow-lg transition-all focus:outline-none">
          @if (isSubmitting) {
          <i class="fa fa-spinner fa-spin mr-2"></i> Processing...
          } @else {
          <i class="fa fa-heart mr-2"></i> Donate
          }
        </button>
      </div>
      }
    </form>
  </div>
</div>
}